import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Unlock, ImageOff, Expand } from 'lucide-react';
import { GalleryLightbox } from './GalleryLightbox';

// The permanent record of known ZATO objects. No fabricated provenance —
// unknown facts are marked as pending rather than invented.
const VAULT_OBJECTS = [
  {
    id: 'OBJECT_001',
    title: 'BITCOIN LIZZY',
    type: 'PHYSICAL ARTWORK',
    year: '2022',
    status: 'ARCHIVED',
    description: "At first glance, an image of one of Britain's most recognisable symbols of monetary and political authority. Look closer. Bitcoin symbols are concealed within the details — cut into the crown and worked into the necklace. The work places an old symbol of value beside a new one.",
    img: '/vault-bitcoin-lizzy.png',
    mediaType: 'image',
  },
  {
    id: 'OBJECT_002',
    title: 'NAKAMOTO TIME',
    type: 'PHYSICAL / LIMITED EDITION',
    year: '2022',
    status: 'ARCHIVED',
    description: 'Franck Muller × Zato. Edition of 100. A mechanical timepiece named after the pseudonymous creator of Bitcoin.',
    img: '/vault-nakamoto-time.jpeg',
    mediaType: 'image',
  },
];

function ObjectMedia({ object, revealed }) {
  if (!object.img) {
    return (
      <div className="vault-img-pending" data-testid={`vault-image-pending-${object.id}`}>
        <ImageOff size={22} className="text-white/20" />
        <span className="font-mono text-[10px] tracking-[0.2em] text-white/25 mt-2">[ IMAGE PENDING ]</span>
      </div>
    );
  }
  return (
    <img
      src={object.img}
      alt={object.title}
      className={`gallery-img ${revealed ? 'gallery-img--revealed' : ''}`}
      loading="lazy"
    />
  );
}

function ObjectOverlay({ object, revealed }) {
  return (
    <div className={`gallery-overlay ${revealed ? 'gallery-overlay--active' : ''}`}>
      <div className="gallery-metadata">
        <div className="gallery-meta-row">
          <span className="gallery-meta-label">TYPE</span>
          <span className="gallery-meta-value">{object.type}</span>
        </div>
        <div className="gallery-meta-row">
          <span className="gallery-meta-label">STATUS</span>
          <span className="gallery-meta-value">{object.status}</span>
        </div>
        <div className="gallery-meta-row">
          <span className="gallery-meta-label">YEAR</span>
          <span className="gallery-meta-value">{object.year || '[PENDING]'}</span>
        </div>
      </div>
    </div>
  );
}

function ObjectBody({ object }) {
  return (
    <div className="gallery-card-body">
      <div className="flex items-start justify-between gap-2">
        <h3 className="gallery-card-title" data-testid={`vault-object-title-${object.id}`}>{object.title}</h3>
        <span className="gallery-year">{object.year || '—'}</span>
      </div>
      <p className="gallery-medium">{object.type}</p>
      <p className="gallery-description">{object.description}</p>
      <div className="flex items-center gap-2 mt-3">
        <span className="font-mono text-[10px] text-white/25">{object.id}</span>
      </div>
    </div>
  );
}

function VaultObjectCard({ object, index, onOpen }) {
  const [revealed, setRevealed] = useState(false);
  const openable = Boolean(object.img);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="gallery-card"
      data-testid={`vault-object-card-${object.id}`}
    >
      <div
        className="gallery-card-inner"
        onMouseEnter={() => setRevealed(true)}
        onMouseLeave={() => setRevealed(false)}
        onClick={() => openable && onOpen(index)}
        style={{ cursor: openable ? 'pointer' : 'default' }}
      >
        <div className="gallery-img-container">
          <ObjectMedia object={object} revealed={revealed} />
          <ObjectOverlay object={object} revealed={revealed} />

          <div className="gallery-lock-icon">
            {revealed ? <Unlock size={14} className="text-[#FFC700]" /> : <Lock size={14} className="text-white/40" />}
          </div>
          {revealed && openable && (
            <div className="absolute bottom-3 right-3">
              <Expand size={12} className="text-[#FFC700]/70" />
            </div>
          )}
        </div>

        <ObjectBody object={object} />
      </div>
    </motion.div>
  );
}

export function GallerySection() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const openableObjects = VAULT_OBJECTS.filter((o) => o.img);

  const handleOpen = (index) => {
    const obj = VAULT_OBJECTS[index];
    const openIndex = openableObjects.findIndex((o) => o.id === obj.id);
    if (openIndex >= 0) setLightboxIndex(openIndex);
  };
  const handleClose = () => setLightboxIndex(null);
  const handleNav = (delta) => {
    setLightboxIndex((prev) => (prev + delta + openableObjects.length) % openableObjects.length);
  };

  return (
    <section id="vault" className="py-32 px-6 relative" data-testid="vault-section">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00ff99]/20 to-transparent" />

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="mono-tag text-[#00ff99]">// SECTION_03 :: PHYSICAL_ARCHIVE</span>
          <h2 className="section-title mt-4">THE<br /><span className="text-[#00ff99]">VAULT</span></h2>
          <p className="body-text text-white/40 mt-4 max-w-lg">
            The permanent record of known ZATO objects. Hover to reveal detail. Click to view in full.
          </p>
        </motion.div>

        <div className="gallery-grid" data-testid="vault-grid">
          {VAULT_OBJECTS.map((object, i) => (
            <VaultObjectCard key={object.id} object={object} index={i} onOpen={handleOpen} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="font-mono text-xs text-white/20">
            MORE OBJECTS PENDING CATALOGUE — INQUIRE VIA SECURE CHANNEL
          </p>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <GalleryLightbox
            artworks={openableObjects}
            activeIndex={lightboxIndex}
            onClose={handleClose}
            onNav={handleNav}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
